import { useCallback, useEffect, useRef } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { ApolloError } from '@apollo/client'
import { generatePath, useNavigate } from 'react-router-dom'
import useIdentifyAndCreateSessionMutation from '../../graphQL/mutations/identifyAndCreateSession'
import useAppDispatch from '../useAppDispatch'
import { useAppStore } from '../useAppStore'
import {
  clearLoginState,
  setLoginError,
  setLoginRateLimited,
  setLoginSubmitting,
  setSessionData,
  setSessionId,
} from '../../redux/slices/config'
import type { IConfig, IRoutes } from '../../interfaces'

/**
 * EventManager handler for the login flow (COM-1371).
 *
 * Fires the `identifyAndCreateSession` mutation with the shopper's store
 * code, writes the resulting session into `defaultSlice`, and navigates to
 * the storefront's authored catalog route on success. Classifies failures
 * into the two generic buckets the backend exposes (COM-1367): rate-limited
 * vs. everything else — nothing else is enumerated, so an invalid-store
 * attempt and a server error land on the same message by design.
 *
 * Returns an async function keyed to the `IDENTIFY_AND_CREATE_SESSION`
 * action name; the emit arg is `{ storeCode }`. The handler drives
 * `loginStatus`, `loginError` and `loginRateLimitRetryAfterSeconds` on the
 * slice, so the authored `LoginForm` reads a `loading`, `error` and
 * `rateLimitedMessage` straight off config bindings. It also owns the
 * rate-limit cooldown timer — the pinned `LoginForm` has no timer of its
 * own and disables submit while `rateLimitedMessage` is non-empty, so a
 * cooldown that never cleared would leave the shopper stuck
 * (COM-1371 review).
 */
export const RATE_LIMIT_ERROR_CODES = ['RATE_LIMITED', 'TOO_MANY_REQUESTS']

const FALLBACK_GENERIC_ERROR_MESSAGE = 'We could not sign you in. Try again.'
const FALLBACK_RATE_LIMITED_MESSAGE =
  'Too many attempts. Wait a moment and try again.'

const CATALOG_CONTAINER_TYPE = 'catalog'

interface IdentifyAndCreateSessionArgs {
  storeCode: string
}

interface LoginTexts {
  errorGeneric?: string
  errorRateLimited?: string
}

const readLoginTexts = (config: IConfig | undefined): LoginTexts => {
  const raw = config?.texts?.login as Record<string, unknown> | undefined
  if (!raw) {
    return {}
  }
  const errorGeneric =
    typeof raw.errorGeneric === 'string' ? raw.errorGeneric : undefined
  const errorRateLimited =
    typeof raw.errorRateLimited === 'string' ? raw.errorRateLimited : undefined
  return { errorGeneric, errorRateLimited }
}

/**
 * Find the authored catalog route and substitute `:slug` and `:sessionId`.
 *
 * `config.routes` lists `{ route, container }` entries whose `container`
 * names a `config.containers[name]` entry; the container's `type` is the
 * screen it renders. The first entry whose container has `type: 'catalog'`
 * is the storefront's catalog destination. There is no `/catalog` suffix in
 * `defaultConfig.ts` — the baseline catalog route is `/:slug/:sessionId` —
 * so a hardcoded `${slug}/${sessionId}/catalog` 404s on every storefront
 * that keeps the baseline route table (COM-1371 review).
 *
 * Returns `null` when no catalog route is authored. The caller falls back
 * to `/:slug/:sessionId`, which is the baseline shape, and reports the
 * misconfiguration to Datadog so an operator can fix the storefront's SCS
 * config.
 */
const resolveCatalogDestination = (
  config: IConfig | undefined,
  slug: string,
  sessionId: string,
): string | null => {
  if (!config) {
    return null
  }
  const routes: IRoutes[] = config.routes ?? []
  const match = routes.find((entry) => {
    const container = config.containers?.[entry.container]
    return container?.type === CATALOG_CONTAINER_TYPE
  })
  if (!match) {
    return null
  }
  try {
    return generatePath(match.route, { slug, sessionId })
  } catch (exception) {
    // `generatePath` throws if the route names a param the record does not
    // satisfy — e.g. a `:categoryId` sits between `:slug` and `:sessionId`
    // that nothing here knows how to fill. A storefront whose catalog route
    // takes more than `{ slug, sessionId }` is unusual; fall through to the
    // baseline and report, same as a missing route.
    return null
  }
}

const reportMissingCatalogRoute = (slug: string) => {
  datadogRum.addError(
    new Error(
      `login flow: no authored route with a container of type 'catalog'; falling back to /${slug}/:sessionId`,
    ),
    { feature: 'login', storefrontSlug: slug },
  )
}

export const useIdentifyAndCreateSession = () => {
  const [identifyAndCreateSession] = useIdentifyAndCreateSessionMutation()
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const navigate = useNavigate()

  // Owns the rate-limit cooldown timer. A ref so a late settle of a stale
  // handler closure cannot race the current one, and a cleanup on unmount
  // so the timer cannot dispatch after the EventManager tore down (see the
  // COM-818 unmount note in CLAUDE.md).
  const rateLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearRateLimitTimer = useCallback(() => {
    if (rateLimitTimerRef.current !== null) {
      clearTimeout(rateLimitTimerRef.current)
      rateLimitTimerRef.current = null
    }
  }, [])

  /*
   * Unmount cleanup: cancel the pending timer AND clear the Redux state the
   * timer was going to clear. `loginStatus` and `loginRateLimitRetryAfterSeconds`
   * live on the shared store; without the clear, a shopper who navigates
   * away mid-cooldown and returns (client-side nav, no reload) finds the
   * store still holding the stranded state and `LoginForm` disabled — the
   * timer that would have cleared it died with the previous instance, and
   * the new instance has no notion of when the cooldown ends. The backend
   * remains responsible for enforcing its limit, same stance as the 0/missing
   * `retryAfterSeconds` policy: a second early request is at most one wasted
   * round trip, which the backend will refuse if it meant longer (review
   * follow-up on PR #867).
   */
  useEffect(
    () => () => {
      if (rateLimitTimerRef.current !== null) {
        clearRateLimitTimer()
        dispatch(clearLoginState())
      }
    },
    [clearRateLimitTimer, dispatch],
  )

  return useCallback(
    async ({ storeCode }: IdentifyAndCreateSessionArgs) => {
      // Read store state at handler time, not at render — the mutation runs
      // a round trip later and nothing re-arms this handler when config,
      // storeName or the session's channel moves.
      const state = store.getState().defaultSlice
      const { storeName, config, sessionData } = state
      const workflowChannel = sessionData?.workflow?.channel
      const texts = readLoginTexts(config)
      const genericErrorMessage =
        texts.errorGeneric ?? FALLBACK_GENERIC_ERROR_MESSAGE
      const rateLimitedMessage =
        texts.errorRateLimited ?? FALLBACK_RATE_LIMITED_MESSAGE

      const trimmed = storeCode?.trim()
      if (!trimmed || !storeName) {
        clearRateLimitTimer()
        dispatch(setLoginError({ message: genericErrorMessage }))
        return
      }

      // A new submit cancels any pending cooldown — the shopper has acted,
      // and the backend will answer either way.
      clearRateLimitTimer()
      dispatch(setLoginSubmitting())

      const variables: {
        storefrontName: string
        storeCode: string
        channel?: string
      } = {
        storefrontName: storeName,
        storeCode: trimmed,
      }
      if (workflowChannel) {
        variables.channel = workflowChannel
      }

      try {
        const result = await identifyAndCreateSession({ variables })
        const payload = result.data?.identifyAndCreateSession
        if (!payload?.sessionUid) {
          dispatch(setLoginError({ message: genericErrorMessage }))
          return
        }
        dispatch(setSessionId(payload.sessionUid))
        dispatch(setSessionData(payload.session))
        dispatch(clearLoginState())
        const destination = resolveCatalogDestination(
          config,
          storeName,
          payload.sessionUid,
        )
        if (destination) {
          navigate(destination)
          return
        }
        reportMissingCatalogRoute(storeName)
        navigate(`/${storeName}/${payload.sessionUid}`)
      } catch (exception) {
        if (exception instanceof ApolloError) {
          const retryAfterSeconds = extractRateLimitRetryAfter(exception)
          if (retryAfterSeconds !== undefined) {
            // The backend may say `retryAfterSeconds: 0` or omit it entirely
            // on a conservative rate-limit response. Honour it as "clear
            // right away", since locking the shopper out past the backend's
            // own cooldown is worse than letting them retry one request
            // early — the backend will refuse it again if it meant longer.
            if (retryAfterSeconds <= 0) {
              dispatch(
                setLoginError({
                  message: rateLimitedMessage,
                }),
              )
              return
            }
            dispatch(setLoginRateLimited({ retryAfterSeconds }))
            rateLimitTimerRef.current = setTimeout(() => {
              rateLimitTimerRef.current = null
              dispatch(clearLoginState())
            }, retryAfterSeconds * 1000)
            return
          }
        }
        dispatch(setLoginError({ message: genericErrorMessage }))
      }
    },
    [
      clearRateLimitTimer,
      dispatch,
      identifyAndCreateSession,
      navigate,
      store,
    ],
  )
}

const extractRateLimitRetryAfter = (
  error: ApolloError,
): number | undefined => {
  const first = error.graphQLErrors?.[0]
  const code = first?.extensions?.code
  if (typeof code !== 'string' || !RATE_LIMIT_ERROR_CODES.includes(code)) {
    return undefined
  }
  const retry = first?.extensions?.retryAfterSeconds
  return typeof retry === 'number' && retry >= 0 ? retry : 0
}

export default useIdentifyAndCreateSession
